import React, { memo, useMemo } from 'react';

import { Avatar, Box, Typography } from '@mui/material';
import { isDisplayable } from '@sharedCR/common/PresenceDot/PresenceDot.utils';
import RoleTag from 'app/shared/components/loanApplication/RoleTag';
import { ROLE_TYPES } from 'app/shared/components/loanApplication/RoleTag/RoleTag.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PresenceUserStatus } from 'constants/presence';
import { getConditionalArray } from 'utils/data';
import { getPersonInitials } from 'utils/loanApplication';
import { getIsUserActive, getOtherTabsLabel } from 'utils/presence';

import {
  getTooltipAvatarColorStyles,
  getTooltipStatusDotColorStyles,
  tooltipDetailStyles,
  tooltipHeaderStyles,
  tooltipRolesStyles,
  tooltipStatusDotStyles,
  tooltipStatusStyles,
} from '../../PresenceAvatarGroup.styles';
import { getIdleMinutes, getOpenDurationLabel } from '../../PresenceAvatarGroup.utils';

const tooltipAvatarStyles = { height: '32px', width: '32px', fontSize: '1.2rem' };

const PresenceTooltipContent = ({
  userData,
  presenceData = null,
  entityNameMap = {},
  userColor = null,
}) => {
  const { firstName, lastName, roleName, roleNames } = userData;
  const initials = getPersonInitials({ firstName, lastName });
  const resolvedRoleNames = _.uniq(
    !_.isEmpty(roleNames) ? roleNames : getConditionalArray(!_.isNil(roleName), () => [roleName]),
  );
  const isName = !!(firstName && lastName);
  const displayName = isName ? `${firstName} ${lastName}` : 'Unnamed';

  const isPresent = !_.isNil(presenceData);
  const isActive = isPresent && getIsUserActive(presenceData);
  const isIdle = isPresent && !isActive;
  const idleMinutes = isPresent ? getIdleMinutes(presenceData.lastActivityAt) : 0;

  const statusText = isActive ? PresenceUserStatus.ACTIVE : `Idle since ${idleMinutes} min ago`;
  const otherTabsLabel = isPresent ? getOtherTabsLabel(presenceData.connectionCount) : null;
  const openDurationLabel = isPresent ? getOpenDurationLabel(presenceData.occurredAt) : null;

  const meta = isPresent ? presenceData.metadata : null;
  const entityName =
    meta?.entityName ??
    (isPresent && !_.isNil(presenceData.entityId) ? entityNameMap[presenceData.entityId] : null);

  const locationPath = useMemo(() => {
    if (!isPresent || _.isNil(presenceData.tab)) {
      return null;
    }

    const parts = [presenceData.tab];

    if (isDisplayable(presenceData.subtab)) {
      parts.push(meta?.subtabName ?? _.startCase(presenceData.subtab));
    }

    if (!_.isNil(entityName)) {
      parts.push(entityName);
    }

    if (isDisplayable(presenceData.sectionId)) {
      parts.push(_.startCase(presenceData.sectionId));
    }

    if (!_.isNil(presenceData.subsection)) {
      const subsectionName = meta?.subsectionName ?? entityNameMap[presenceData.subsection];

      if (!_.isNil(subsectionName)) {
        parts.push(subsectionName);
      }
    }

    return parts.join(' → ');
  }, [isPresent, presenceData, entityName, entityNameMap, meta]);

  const avatarCombinedStyles = useMemo(
    () => ({ ...tooltipAvatarStyles, ...getTooltipAvatarColorStyles({ userColor }) }),
    [userColor],
  );

  const statusDotCombinedStyles = useMemo(
    () => ({
      ...tooltipStatusDotStyles,
      ...getTooltipStatusDotColorStyles({ isActive, isIdle }),
    }),
    [isActive, isIdle],
  );

  return (
    <Box>
      <Box sx={tooltipHeaderStyles}>
        <Avatar sx={avatarCombinedStyles}>{initials}</Avatar>
        <Box>
          <Typography color="text.primary" variant="medium14">
            {displayName}
          </Typography>
          {!_.isEmpty(resolvedRoleNames) && (
            <Box sx={tooltipRolesStyles}>
              {resolvedRoleNames.map(name => (
                <RoleTag key={name} roleType={ROLE_TYPES.INTERNAL}>
                  {name}
                </RoleTag>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {!_.isNil(locationPath) && (
        <Typography color="text.secondary" sx={tooltipDetailStyles} variant="regular12">
          Viewing: {locationPath}
        </Typography>
      )}

      {!_.isNil(otherTabsLabel) && (
        <Typography color="text.secondary" sx={tooltipDetailStyles} variant="regular12">
          {otherTabsLabel}
        </Typography>
      )}

      {!_.isNil(openDurationLabel) && (
        <Typography color="text.secondary" sx={tooltipDetailStyles} variant="regular12">
          {openDurationLabel}
        </Typography>
      )}

      {isPresent && (
        <Box sx={tooltipStatusStyles}>
          <Box sx={statusDotCombinedStyles} />
          <Typography color="text.secondary" sx={tooltipDetailStyles} variant="regular12">
            {statusText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

PresenceTooltipContent.propTypes = {
  userData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    roleName: PropTypes.string,
    roleNames: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  presenceData: PropTypes.shape({
    status: PropTypes.string,
    tab: PropTypes.string,
    sectionId: PropTypes.string,
    entityId: PropTypes.string,
    subsection: PropTypes.string,
    lastActivityAt: PropTypes.string,
    metadata: PropTypes.object,
    connectionCount: PropTypes.number,
    occurredAt: PropTypes.number,
  }),
  entityNameMap: PropTypes.object,
  userColor: PropTypes.shape({
    bg: PropTypes.string,
    fg: PropTypes.string,
    dot: PropTypes.string,
  }),
};

export default memo(PresenceTooltipContent);
